import type { CSSProperties, ReactNode } from 'react';
import { cn, Table } from '@herit-fe/heritage-ui';
import { ELEVATED_SURFACE } from './elevated-card';
import { ScrollArea } from './scroll-area';

/**
 * 공용 스타일 테이블 루트 — 프로젝트 모든 표의 일관 chrome(외곽 카드 보더·라운드·헤더/푸터 배경·셀 패딩 +
 * 선택적 세로선)을 한 곳에서 입힌다(테이블 통일 SSOT). 내부는 heritage `Table` 위에 descendant-selector 로
 * **스타일만** 주입하므로, 호출처는 heritage `TableHeader/TableRow/TableHead/TableCell/TableFooter` 를
 * 자유 조립한다 — 멀티헤더·합계 footer·셀 병합(rowSpan/colSpan) 전부 가능.
 *
 * - `shell`(기본 true): 외곽 = **카드와 동일**(`ELEVATED_SURFACE` + `rounded-xl bg-card` — 테두리 대신
 *   drop-shadow 로 띄운 면). 카드 영역 대체용이라 표 자체가 콘텐츠 배경 위에 뜬다. 이미 카드 안이면 false.
 * - `grid`(기본 false): 컬럼 구분 **세로선**을 heritage 행 보더(가로선) 위에 덧댄다(끝열 제외). perimeter(외곽)는
 *   shell/바깥 카드가 담당 → list 표와 **동일 chrome** + 세로선(이중 보더·모서리 깨짐 없음). 통계·현황판 다열 표용.
 * - `scroll`(기본 false): 위에 **세로 고정높이**(max-h-80) + **thead/tfoot 고정(sticky)** 을 얹는다(고정높이 카드용:
 *   설비 구성·이벤트 내역). 내용 적으면 자연 높이. sticky 셀은 아래 행이 비치지 않게 **불투명 배경**(알파 금지).
 *
 * 가로 오버플로우는 **모드 무관 항상 heritage `ScrollArea`(base-ui) 오버레이 스크롤바**로 통일(폭 좁힘 시 네이티브
 * 바 노출 방지) — heritage table-container 의 `overflow-x-auto` 를 무력화하고 ScrollArea Viewport 가 가로를 소유.
 * `scroll` 은 여기에 세로 고정높이+sticky 만 추가(비-scroll 은 세로 자연높이·페이지 흐름·헤더 비고정).
 */
export function DataTable({
  children,
  className,
  shell = true,
  grid = false,
  scroll = false,
  maxHeight = '20rem',
}: {
  children: ReactNode;
  className?: string;
  shell?: boolean;
  grid?: boolean;
  scroll?: boolean;
  /** `scroll` 일 때 세로 고정높이 상한(px 숫자 또는 CSS 길이). 기본 20rem(=구 max-h-80). 넘치면 내부 스크롤. */
  maxHeight?: number | string;
}) {
  const maxHeightCss = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
  const table = (
    <Table
      className={cn(
        // 세로선(grid) — heritage 행 보더(가로선) 위에 컬럼 구분 세로선만 덧댄다(끝열 제외, border-r). perimeter 는
        // shell 의 rounded 보더(또는 바깥 카드)가 담당 → 이중 보더·모서리 깨짐 없이 list 표와 동일 chrome + 세로선.
        grid && '[&_:where(td,th):not(:last-child)]:border-r [&_:where(td,th)]:border-border',
        // 헤더/푸터 배경 + 합계 강조. **불투명**(알파 X) — 알파를 섞으면 카드 흰 배경에 묻히고, sticky 일 때는
        // 아래 행이 비쳐 보인다.
        //
        // light 는 카드(흰색)보다 **어둡게**(gray-50), dark 는 카드(gray-800)보다 **밝게**(gray-700) — 방향이
        // 반대인 건 의도다. dark 에서 어둡게 잡으면 콘텐츠 배경(`--heviton-bg-tinted` = gray-900)과 **같은 색**이라
        // 표 상단이 페이지 배경에 녹아 카드 경계가 사라진다. dark 는 밝을수록 위로 뜨는 관례와도 맞는다.
        '[&_thead]:bg-gray-50 dark:[&_thead]:bg-gray-700',
        '[&_tfoot]:bg-gray-50 dark:[&_tfoot]:bg-gray-700 [&_tfoot]:font-medium',
        '[&_th]:p-3.5 [&_td]:p-3.5',
        // sticky thead(상단)·tfoot(하단, 합계) — 불투명 bg 로 스크롤 행 가림. z 로 행 위.
        //
        // 구분선은 **th/td 자신의 inset box-shadow** 로 그린다. heritage 는 구분선을 `thead>tr` 의 `border-b` ·
        // `tfoot` 의 `border-t` 로 갖는데, sticky 가 걸리는 건 `th`/`td`(셀)이라 그 보더들은 같이 고정되지 않고
        // 본문과 함께 스크롤돼 사라진다. border 대신 box-shadow 인 이유: border-collapse 표에서 셀 보더는
        // 인접 셀과 병합돼 sticky 시 깨진다.
        // 원래 보더는 **꺼야 한다** — 안 끄면 스크롤 전(최상단)에 heritage 보더 + inset shadow 가 겹쳐 2줄이 된다.
        scroll &&
          '[&_thead_tr]:border-0 [&_tfoot]:border-0 [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20 [&_thead_th]:bg-gray-50 dark:[&_thead_th]:bg-gray-700 [&_thead_th]:shadow-[inset_0_-1px_0_var(--border)] [&_tfoot_td]:sticky [&_tfoot_td]:bottom-0 [&_tfoot_td]:z-20 [&_tfoot_td]:bg-gray-50 dark:[&_tfoot_td]:bg-gray-700 [&_tfoot_td]:shadow-[inset_0_1px_0_var(--border)]',
        className,
      )}
    >
      {children}
    </Table>
  );
  // 모든 표를 heritage `ScrollArea`(base-ui) 로 감싼다 — 가로 오버플로우 스크롤바를 **항상 오버레이(ScrollArea)로
  // 통일**(heritage table-container 의 네이티브 `overflow-x-auto` 를 visible 로 무력화 → ScrollArea Viewport 가 가로
  // 소유). 오버레이 바(hover)라 gutter 미점유·표 위 겹침.
  //
  // **방향(orientation) 을 scroll 로 분기**: 비-scroll 은 `horizontal` 만 — base-ui Viewport 가 세로 오버플로우까지
  // 가져가면(both) `max-h` 가 없어 안 잘려도 **세로 휠 이벤트를 삼켜** 바깥 페이지 스크롤로 전파가 끊긴다(표 위에서
  // 휠 먹통의 원인). 가로만 잡으면 세로 휠은 그대로 통과해 페이지가 스크롤된다. `scroll` 일 때만 `both` + 세로 상한
  // (`maxHeight`, 인라인 style — 소비처가 표별 높이 지정) + sticky thead/tfoot.
  return (
    <ScrollArea
      orientation={scroll ? 'both' : 'horizontal'}
      scrollbarVisibility="hover"
      scrollbarInsetX
      scrollbarInsetY={scroll}
      // **두 모드 모두** 체이닝 허용. 비-scroll 표는 base-ui Viewport 가 `overflow:scroll`(양축)이라 세로 내용이
      // 안 넘쳐도 contain 이면 세로 휠을 먹는다. scroll(고정높이) 표도 끝에 닿으면 페이지로 넘어가야 한다
      // (안 그러면 커서를 표 밖으로 빼야 스크롤된다) — 문서 바운스 차단은 `app-layout` 스크롤러가 소유한다.
      overscrollContain={false}
      style={scroll ? ({ '--table-max-h': maxHeightCss } as CSSProperties) : undefined}
      className={cn(
        // 가로 통일(두 모드 공통) — table-container overflow 무력화로 Viewport 를 가로 스크롤 컨테이너로.
        '**:data-[slot=table-container]:overflow-visible!',
        // scroll 시에만 Viewport 세로 높이 캡(내용 적으면 자연 높이·많으면 스크롤). 높이는 `--table-max-h`(위 style)로 주입.
        scroll && '**:data-[slot=scroll-area-viewport]:max-h-(--table-max-h)',
        // 표 껍데기 = 카드와 **동일 표면 규칙**(`ELEVATED_SURFACE`: 테두리 대신 drop-shadow). 소비처 3곳
        // (수익·발전량 결과표, 계측 결과표) 모두 콘텐츠(회색) 배경 위에 직접 놓여, 같은 화면의 리포트 카드와
        // chrome 이 어긋나지 않아야 한다. 카드 **안**에 넣을 땐 `shell={false}` 가 이미 껍데기를 끈다.
        shell && cn(ELEVATED_SURFACE, 'rounded-xl bg-card'),
      )}
    >
      {table}
    </ScrollArea>
  );
}
